import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import AdminNavbar from '../components/AdminNavbar';
import { DollarSign, Save, RefreshCw } from 'lucide-react';

const AdminFeeSettingsPage = () => {
    const [settings, setSettings] = useState({
        tradingFee: { maker: 0.1, taker: 0.1 },
        withdrawalFees: {},
        p2pFee: 0,
        futuresFee: { maker: 0.02, taker: 0.04 }
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => { fetchSettings(); }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const res = await api.get('/admin/settings/fees');
            if (res.data.feeSettings) setSettings(res.data.feeSettings);
        } catch (error) {
            toast.error('Failed to load fee settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await api.put('/admin/settings/fees', { feeSettings: settings });
            toast.success('Fee settings saved');
        } catch (error) {
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="min-h-screen bg-slate-900"><AdminNavbar /><div className="text-center py-12 text-gray-400">Loading...</div></div>;

    return (
        <div className="min-h-screen bg-slate-900">
            <AdminNavbar />
            <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><DollarSign className="text-green-400" /> Fee Settings</h1>
                        <p className="text-gray-400 text-sm">Configure trading and withdrawal fees</p>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={fetchSettings} className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"><RefreshCw size={18} /></button>
                        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 disabled:opacity-50">
                            <Save size={18} /> {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="p-6 max-w-4xl">
                <div className="grid gap-6">
                    {/* Spot Trading Fees */}
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                        <h2 className="text-lg font-bold text-white mb-4">Spot Trading Fees (%)</h2>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-gray-400 text-sm">Maker Fee</label>
                                <input type="number" step="0.01" value={settings.tradingFee.maker} onChange={e => setSettings({ ...settings, tradingFee: { ...settings.tradingFee, maker: parseFloat(e.target.value) } })} className="w-full mt-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                            </div>
                            <div>
                                <label className="text-gray-400 text-sm">Taker Fee</label>
                                <input type="number" step="0.01" value={settings.tradingFee.taker} onChange={e => setSettings({ ...settings, tradingFee: { ...settings.tradingFee, taker: parseFloat(e.target.value) } })} className="w-full mt-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                            </div>
                        </div>
                    </div>

                    {/* Futures Trading Fees */}
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                        <h2 className="text-lg font-bold text-white mb-4">Futures Trading Fees (%)</h2>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-gray-400 text-sm">Maker Fee</label>
                                <input type="number" step="0.01" value={settings.futuresFee.maker} onChange={e => setSettings({ ...settings, futuresFee: { ...settings.futuresFee, maker: parseFloat(e.target.value) } })} className="w-full mt-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                            </div>
                            <div>
                                <label className="text-gray-400 text-sm">Taker Fee</label>
                                <input type="number" step="0.01" value={settings.futuresFee.taker} onChange={e => setSettings({ ...settings, futuresFee: { ...settings.futuresFee, taker: parseFloat(e.target.value) } })} className="w-full mt-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                            </div>
                        </div>
                    </div>

                    {/* P2P Fee */}
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                        <h2 className="text-lg font-bold text-white mb-4">P2P Trading Fee (%)</h2>
                        <input type="number" step="0.01" value={settings.p2pFee} onChange={e => setSettings({ ...settings, p2pFee: parseFloat(e.target.value) })} className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminFeeSettingsPage;
